import {
  Component,
  HostListener,
  HostBinding,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  AfterViewChecked,
  AfterViewInit,
  ViewChild,
  ElementRef,
  NgZone,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { Angulartics2GoogleAnalytics } from 'angulartics2';
import { MatDialog } from '@angular/material/dialog';

import { TelemetryService } from 'src/app/services/telemetry.service';

import { lastValueFrom, combineLatest, Observable, fromEvent } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  delay,
  startWith,
  map,
} from 'rxjs/operators';
import { CdkDragStart } from '@angular/cdk/drag-drop';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DeviceDetectorService } from 'ngx-device-detector';
import { SettingsService } from 'src/app/services/settings.service';
import * as moment from 'moment';
import { DateAdapter } from '@angular/material/core';
import { Location } from '@angular/common';
import { environment } from '../environments/environment';
import { ThemeService } from './services/theme.service';
import { AuthService } from './services/auth.service';
import { PwaService } from './services/pwa-service.service';
import { UserService } from './services/user.service';
import { PaymentService } from './services/payment.service';
import { NavService } from './services/nav.service';
import { QuestionsService } from './services/questions.service';
import { ResourcesService } from './services/resources.service';
import { SetTimeLocaleService } from './services/set-time-locale.service';
import { AclService, Feature } from './services/acl.service';
import { UiService } from './services/ui.service';
import { SessionSharedDataService } from './services/session-shared-data.service';
import { ScreenSizeService } from './services/screen-size.service';

import { FlagsService, FLAGS } from './services/flags.service';
import { EventService } from './services/event.service';
import { User, UserData, UserInfo } from './models/user';
import { NewMessageDialogComponent } from './messages/new-message-dialog/new-message-dialog.component';
import { MessagingService } from './services/messaging.service';
import {
  NewConversationRequest,
  NewGroupConversationRequest,
  NewUserConversationRequest,
} from './models/messaging';
import { isUserConversation } from './common/utils/messaging';
import { URL_CONSTANTS } from './common/utils/url';
import { getCodeTriggeredNavigationState, SideNavNavigationState } from './common/utils/nav-state';
import { URLService } from './services/dynamic-url.service';
import { RevokedInfoDialogComponent } from './dialogs/revoked-info-dialog/revoke-info-dialog.component';
import { ProfilesService } from './services/profiles.service';
import {
  InspectModeMessage,
  FollowingDataMessage,
  InspectModeRepository,
  PostMessageType,
} from './state/inspect-mode-repository.service';
import { DomListenerFactoryService } from './services/dom-listener-factory.service';
import { DomListener } from './utilities/DomListener';
import { pencilLogoText } from './app.constants';
import { panelID } from './settings/settings.constants';
import { SUBSCRIPTION_TYPES, SubscriptionStatus } from './models/payment';
import { SpaceRepository } from './state/space.repository';
import { ReferralService } from './services/referral.service';
import { ModalManagerService } from './services/modal-manager.service';
import { ReferralAcceptedDialogComponent } from './common/referral-accepted-dialog/referral-accepted-dialog.component';
import { DeviceAndBrowserDetectorService } from './services/device-and-browser-detector.service';
import {
  PaywallIndicatorService,
  UpgradePlanEvaluationType,
} from './services/paywall-indicator.service';
import { CountedMetric } from './services/performance.logger.service';
import { ForegroundActivityService } from './services/foreground-activity.service';
import { NgZoneHolderService } from './services/ng-zone-holder.service';
import { UploadFileService } from './services/upload-file.service';
import { IconTypes } from './standalones/components/pencil-icon/pencil-icon.component';
import {
  ActionsQueryParamKeys,
  checkIfQueryParamSetToTrue,
  removeQueryParamsFromUrl,
} from './common/utils/actions-query-params-util';
import { InstitutionalPlanUpgradeSuccessModalComponent } from './dialogs/institutional-plan-upgrade-success-modal/institutional-plan-upgrade-success-modal.component';
import { AuthTracing } from './utilities/auth-tracing.utils';
import { isNativeApp } from './utilities/native-app.utils';
import {
  PushNotificationsService,
  NotificationPermissionStatus,
} from './services/push-notifications.service';
import { LazyComponentLoaderService } from './services/lazy-component-loader.service';
import { deleteRecordingsMarkedForDeletionOnNextOpen } from './sessions/session/wb-video-controls/recording-db';
import { TourService } from './services/tours/tour.service';
import { TouchScrollLockService } from './services/touch-scroll-lock.service';
import {
  NavbarViewport,
  RouteNavbarItemConfig,
  getRouteNavbarItemConfigFromUrl,
} from './nav-bar/nav-bar.component';

import { SpacesSwitcherService } from './services/spaces-switcher.service';
const MIN_KEYBOARD_HEIGHT = 300; // This might not always be correct

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  @HostBinding('class.app-mobile-view') isMobileView = false;
  @HostBinding('class.is-keyboard-open') isKeyboardOpen = false;

  @ViewChild('applicationContent')
  public applicationContentElement!: ElementRef<HTMLElement>;

  @ViewChild('dynamicGadgets', { read: ViewContainerRef })
  dynamicGadgetsContainer!: ViewContainerRef;

  /**
   * Fires when the sidenav enters/leaves the DOM. Publish its width on show and 0 on hide, giving
   * the session's viewport math the sidebar's contribution without the canvas code reaching into
   * the shell's DOM. See {@link PanelsWidth.sidebarWidth}.
   */
  // `read: ElementRef` because the ref now sits on <mat-sidenav>; without it Angular hands back the
  // MatSidenav instance and there is no nativeElement to measure.
  @ViewChild('appAside', { read: ElementRef })
  set appAside(ref: ElementRef<HTMLElement> | undefined) {
    this.appAsideEl = ref?.nativeElement;
    // Cache the natural (open) width the first time the sidenav mounts; once closed the element
    // measures 0, so we can't re-measure it to restore the open width.
    const width = this.appAsideEl?.offsetWidth ?? 0;
    if (width > 0) {
      this.expandedSidebarWidth = width;
    }
    this.publishSidebarWidth();
  }

  private appAsideEl?: HTMLElement;
  private expandedSidebarWidth = 0;
  // A non-desktop sidenav overlays the content (`over` mode) instead of taking layout space.
  private isDesktopViewport = true;

  /**
   * Publishes the sidebar's contribution to the session viewport math. Closing keeps the element
   * mounted, so the {@link appAside} setter doesn't refire — we recompute from the open state
   * instead: 0 while closed, the cached open width otherwise.
   */
  private publishSidebarWidth(): void {
    const open = this.navService.isMenuOpened.getValue();
    // Nothing mounted, closed, or overlaying the content (non-desktop `over` mode) → no width.
    const width =
      !this.appAsideEl || !open || !this.isDesktopViewport ? 0 : this.expandedSidebarWidth;
    // Session viewport math (pan/zoom bounds) and responsive breakpoints (panel bottom-sheets,
    // bottom toolbar) both need the sidebar's footprint; publish the single computed value to each.
    this.sessionSharedDataService.setPanelWidth({ sidebarWidth: width });
    this.screenSizeService.setSidebarWidth(width);
  }

  public appLoading = false;

  public showLoginFlowLoader: Observable<boolean>;
  // Drives the global sidebar visibility via the async pipe (avoids calling a function in the
  // template). Recomputed on authentication changes and on each navigation.
  public showSideNavBar$: Observable<boolean>;
  hidesNavForSessionDetails = false;
  // null when the sidenav is hidden; otherwise carries desktop mode for drawer bindings.
  public sidenavViewport$: Observable<{ isDesktop: boolean } | null>;
  // Compact top-bar config for the current route (null when hidden).
  public topBar$: Observable<RouteNavbarItemConfig | null>;
  isUploading = false;
  showEditor = false;
  fullScreen = false;
  dragElement: any;
  env = environment;
  user?: User;
  Features = Feature;
  URL_CONSTANTS = URL_CONSTANTS;

  showNotificationBell = false;
  isRevokedModalShown = false;
  isListeningToUserLogout = false;
  pencilLogoText = pencilLogoText;
  domListener: DomListener;
  planEvaluationTypes = UpgradePlanEvaluationType;
  isFreeUser = false;
  isInspectMode = this.inspectModeRepository.isInspectMode;
  /** Gates switcher-only sidebar chrome. Re-emits once the flags finish loading. */
  readonly isSpacesSwitcherEnabled$ = this.spacesSwitcherService.isEnabled$;

  isNativeApp = isNativeApp();
  protected readonly IconTypes = IconTypes;
  urlToHeaderIconMap: { [key: string]: string } = {
    '/files': 'folder_header',
    '/schedule': 'schedule_header',
    '/sessions': 'clipboard-clock',
    '/analytics': 'analytics',
    '/content': 'courses_header',
    '/profiles': 'people_header',
    '/messages': 'messages_header',
    '/alerts': 'alert-triangle',
  };

  urlToHeaderIconTypeMap: { [key: string]: IconTypes } = {
    '/files': IconTypes.svgIcon,
    '/schedule': IconTypes.svgIcon,
    '/sessions': IconTypes.lucideIcon,
    '/analytics': IconTypes.svgIcon,
    '/content': IconTypes.svgIcon,
    '/profiles': IconTypes.svgIcon,
    '/messages': IconTypes.svgIcon,
    '/alerts': IconTypes.svgIcon,
  };

  get currentUrlIcon(): string {
    const urlSegment = Object.keys(this.urlToHeaderIconMap).find((segment) => {
      if (segment === '/content') {
        return this.router.url === segment;
      }

      return this.router.url.includes(segment);
    });

    return urlSegment ? this.urlToHeaderIconMap[urlSegment] || '' : '';
  }

  get currentUrlIconType(): IconTypes {
    const urlSegment = Object.keys(this.urlToHeaderIconTypeMap).find((segment) =>
      this.router.url.includes(segment),
    );

    return urlSegment ? this.urlToHeaderIconTypeMap[urlSegment] : IconTypes.svgIcon;
  }

  constructor(
    public authService: AuthService,
    public pwaService: PwaService,
    public router: Router,
    public userService: UserService,
    private paymentService: PaymentService,
    public navService: NavService,
    public questionsService: QuestionsService,
    public resourceService: ResourcesService,
    public deviceService: DeviceDetectorService,
    private updates: SwUpdate,
    private flagsService: FlagsService,
    private spacesSwitcherService: SpacesSwitcherService,
    private angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
    public eventService: EventService,
    private gtmService: GoogleTagManagerService,
    private aclService: AclService,
    private messagingService: MessagingService,
    private dialog: MatDialog,
    private setTimeLocaleService: SetTimeLocaleService,
    public uRLService: URLService,
    private cd: ChangeDetectorRef,
    public profilesService: ProfilesService,
    private uiService: UiService,
    private telemetry: TelemetryService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private settingsService: SettingsService,
    private spaceRepo: SpaceRepository,
    private referralService: ReferralService,
    private modalManagerService: ModalManagerService,
    public activatedRoute: ActivatedRoute,
    private domListenerFactoryService: DomListenerFactoryService,
    private paywallIndicatorService: PaywallIndicatorService,
    private foregroundActivityService: ForegroundActivityService,
    private zone: NgZone,
    private uploadService: UploadFileService,
    private dateAdapter: DateAdapter<any>,
    // this is to initialize ngZone static variable to be used anywhere on the app
    protected ngZoneHolderService: NgZoneHolderService,
    private pushNotificationsService: PushNotificationsService,
    private location: Location,
    private telemetryService: TelemetryService,
    // this is to initialize the theme service only
    protected _themeService: ThemeService,
    private inspectModeRepository: InspectModeRepository,
    private lazyComponentLoader: LazyComponentLoaderService,
    private tourService: TourService,
    private touchScrollLockService: TouchScrollLockService,
    private sessionSharedDataService: SessionSharedDataService,
    public screenSizeService: ScreenSizeService,
  ) {
    this.domListener = this.domListenerFactoryService.createInstance();
    this.isMobileView = this.deviceAndBrowserDetectorService.isMobile();
    if (!this.isMobileView) {
      this.domListener.add(window, 'resize', this.onResize.bind(this));
      // Emit first value based on current width till hitting the first resize event
      this.uiService.isMobile.next(window.innerWidth <= this.uiService.MOBILE_MAX_WIDTH);
    } else {
      this.uiService.isMobile.next(this.isMobileView);
    }
    this.isKeyboardOpen =
      this.isMobileView &&
      window.screen.height - MIN_KEYBOARD_HEIGHT > (window.visualViewport?.height as number);
    this.domListener.add(window.visualViewport, 'resize', () => {
      this.isKeyboardOpen =
        this.isMobileView &&
        window.screen.height - MIN_KEYBOARD_HEIGHT > (window.visualViewport?.height as number);
    });

    this.showLoginFlowLoader = this.userService.showLoginFlowLoader$;

    // Registered before showSideNavBar$ so the flag is current when that stream reacts to the
    // same NavigationEnd, which is also the last point the navigation's state can be read.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => this.updateSessionDetailsNavVisibility());

    this.showSideNavBar$ = combineLatest([
      this.authService.firebaseUser,
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
      ),
    ]).pipe(
      map(([firebaseUser]) => !!firebaseUser && this.showSideNavBar(this.router.url)),
      distinctUntilChanged(),
    );

    this.sidenavViewport$ = combineLatest([
      this.showSideNavBar$,
      this.screenSizeService.isDesktop$,
    ]).pipe(
      map(([showSideNavBar, isDesktop]) => (showSideNavBar ? { isDesktop } : null)),
      distinctUntilChanged(
        (prev, curr) => prev?.isDesktop === curr?.isDesktop && !!prev === !!curr,
      ),
    );

    // Compact top bar is non-desktop only; always match Mobile/Both route config so
    // Desktop-only map entries never drive it.
    this.topBar$ = combineLatest([
      this.screenSizeService.isDesktop$,
      this.navService.navigationEndUrl$,
    ]).pipe(
      map(([isDesktop, url]) => {
        if (isDesktop) {
          return null;
        }

        return getRouteNavbarItemConfigFromUrl(url, NavbarViewport.Mobile) ?? null;
      }),
      distinctUntilChanged((prev, curr) => prev?.item === curr?.item),
    );

    // Overlay-open state must update on desktop/mobile transitions.
    // Force CD after the sync so MatSidenav remeasures on orientation/resize flips.
    this.screenSizeService.isDesktop$.pipe(untilDestroyed(this)).subscribe((isDesktop) => {
      this.isDesktopViewport = isDesktop;
      this.navService.isMenuOpened.next(isDesktop);
      this.publishSidebarWidth();
      this.cd.detectChanges();
    });

    this.userService.user
      .pipe(
        distinctUntilChanged((a, b) => a?.user._id === b?.user._id),
        untilDestroyed(this),
      )
      .subscribe((data) => {
        if (data?.user) {
          this.paymentService.handlePendingUpgradeFallbackIfNeeded(data);
        }
      });

    /**
     * subscribe to the change of the language to set the locale for the date adapter used for mat-calendar component
     */
    this.userService.selectedLangCode.pipe(untilDestroyed(this)).subscribe((selectedLang) => {
      this.dateAdapter.setLocale(selectedLang);
    });

    this.userService.rtl.pipe(untilDestroyed(this)).subscribe((res) => {
      if (res) {
        document.body.classList.add('rtl');
      } else {
        document.body.classList.remove('rtl');
      }
    });
    this.userService.user
      .pipe(
        filter((val) => !!val),
        take(1),
        untilDestroyed(this),
      )
      .subscribe((res) => {
        if (res?.user?._id) {
          this.isFreeUser = this.paywallIndicatorService.isUserInSubscription(
            SUBSCRIPTION_TYPES.FREE,
          );

          this.referralService
            .getAcceptedReferrals(res?.user?._id)
            .pipe(untilDestroyed(this))
            .subscribe((referralsRes) => {
              const referrals = referralsRes?.referrals?.filter(
                (r) =>
                  r.referred_user?.billingStatus?.subscription_status === SubscriptionStatus.ACTIVE,
              );
              if (referrals?.length) {
                const unviewedReferrals = referrals.filter((r) => !r.viewed);
                if (unviewedReferrals.length) {
                  this.showReferralAcceptedDialog(
                    unviewedReferrals[0]._id,
                    res?.user,
                    unviewedReferrals[0].referred_user,
                  );
                }
                this.referralService.upgradedReferrals.next(referrals);
              }
            });
        }
      });
    this.initRouterEvents();
    this.renderOutOfZoneNavigations();

    this.angulartics2GoogleAnalytics.startTracking();

    // eslint-disable-next-line frontend-rules/ngx-translate-service
    window.addEventListener('beforeinstallprompt', (e) => {
      this.pwaService.deferredPrompt = e;
    });

    this.userService.isUploading.pipe(untilDestroyed(this)).subscribe((res) => {
      this.isUploading = res;
    });

    this.authService.firebaseUser.pipe(untilDestroyed(this)).subscribe((user) => {
      if (!user) {
        return;
      }

      // Check if push notifications are supported
      if (this.pushNotificationsService.isPushSupported) {
        // Check if we already have an active subscription, then check permission status
        this.pushNotificationsService
          .getExistingSubscription()
          .pipe(
            take(1),
            filter((subscription) => !subscription),
            switchMap(() => this.pushNotificationsService.permissionStatus$),
            take(1),
            filter((status) => status === NotificationPermissionStatus.DEFAULT),
            // Delay requesting permission to not overwhelm the user right away
            // Listen for any click event on the window to trigger notification permission
            // This ensures we have user interaction before requesting permission
            // Using RxJS fromEvent for better integration with Angular's reactive patterns
            switchMap(() => fromEvent(window, 'click').pipe(take(1))),
            delay(PushNotificationsService.NOTIFICATION_PROMPT_DELAY_MS),
            untilDestroyed(this),
          )
          .subscribe(() => {
            this.enableNotifications();
          });
      }
    });

    this.router.events.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.forceHideStaticLoader(this.router.url)) {
        this.hideStaticLoader(); // Hide loader for cases where userLoading never shows
      }
    });

    this.showNotificationBell = this.flagsService.isFlagEnabled(FLAGS.NOTIFICATIONS_BELL);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.uploadService.resetUploadingCounter();
        this.userService.setLoginFlowLoading(false);
        this.tourService.handleRouteChange(this.router.url);

        // Check for institution upgrade modal after navigation is complete and all loading is done
        this.checkForInstitutionUpgradeAfterFullLoad();
      });

    this.updates.unrecoverable.pipe(untilDestroyed(this)).subscribe((res) => {
      this.telemetry.event('Unrecoverable SW update error', {
        reason: res.reason,
      });
      if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_REFRESH_AFTER_UNRECOVERABLE_UPDATE_ERROR)) {
        window.location.reload();
      }
    });
  }

  /**
   * Enable push notifications - must be called from a user gesture
   */
  enableNotifications(): void {
    this.pushNotificationsService.requestPermissionAndSubscribe().pipe(take(1)).subscribe();
  }

  ngAfterViewInit(): void {
    this.domListener.add(
      this.applicationContentElement.nativeElement,
      'wheel',
      this.preventWindowZoom.bind(this),
      true,
    );
    this.domListener.add(
      this.applicationContentElement.nativeElement,
      'mousewheel',
      this.preventWindowZoom.bind(this),
      true,
    );
    // Listen to postMessage events outside Angular zone to prevent excessive change detection
    // Handler will run inside zone to avoid regressions
    this.domListener.add(window, 'message', this.onPostMessage.bind(this), true);
    this.hideStaticLoader();
    // Set up dynamic component loading for gadgets
    this.setupDynamicGadgetLoading();
    this.touchScrollLockService.lockPageScrollOnTouchDevices();
  }

  private setupDynamicGadgetLoading(): void {
    // Subscribe to calculator toggle
    this.navService.showCalculator.pipe(untilDestroyed(this)).subscribe((show) => {
      if (show) {
        this.lazyComponentLoader.loadCalculator(this.dynamicGadgetsContainer);
      } else {
        this.lazyComponentLoader.destroyCalculator();
      }
    });

    // Subscribe to graph toggle
    this.navService.showGraph.pipe(untilDestroyed(this)).subscribe((show) => {
      if (show) {
        this.lazyComponentLoader.loadGraph(this.dynamicGadgetsContainer);
      } else {
        this.lazyComponentLoader.destroyGraph();
      }
    });

    // Subscribe to periodic table toggle
    this.navService.showPeriodicTable.pipe(untilDestroyed(this)).subscribe((show) => {
      if (show) {
        this.lazyComponentLoader.loadPeriodicTable(this.dynamicGadgetsContainer);
      } else {
        this.lazyComponentLoader.destroyPeriodicTable();
      }
    });
  }

  hideStaticLoader(): void {
    const loadingDiv = document.getElementById('initloadingtxt');
    loadingDiv?.parentNode?.removeChild(loadingDiv);
  }

  initRouterEvents(): void {
    this.router.events.forEach((item) => {
      if (!this.user) {
        this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
          if (res && res.user) {
            const firebaseUser = this.authService.getCurrentUser();
            if (firebaseUser && (firebaseUser.emailVerified || firebaseUser.isAnonymous)) {
              this.isListeningToUserLogout = true;
            }
            this.user = res?.user;
            this.setTimeLocaleService.setMomentLocale(<string>res.user.settings?.lang_interface);
            // Update timezone in moment globally
            moment.tz.setDefault(res.user.timezone.value);
            this.setupTelemetry(res);
            if (
              !this.isRevokedModalShown &&
              this.user.revoked &&
              this.user.revoked.showRevokedModal
            ) {
              this.isRevokedModalShown = true;
              this.showRevokedInfo();
            }
          }
        });
      }
      if (item instanceof NavigationEnd) {
        const gtmTag = {
          event: 'page',
          pageName: item.url,
        };
        this.pushGTMData(gtmTag);
        this.navService.closeAllGadgets();
      }
    });
  }

  // Zone timers are disabled (index.html), and the router schedules browser
  // back/forward navigations with setTimeout, so they finish outside Angular
  // and the new route would never be rendered without re-entering the zone.
  private renderOutOfZoneNavigations(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd && !NgZone.isInAngularZone()),
        untilDestroyed(this),
      )
      .subscribe(() => this.zone.run(() => {}));
  }

  ngOnInit(): void {
    // Default FullStory Spaces vars before SessionComponent owns them for active Space
    this.telemetry.setSessionVars({ spacesView: 'NOT_SPACES' });

    // Republish the sidebar width when the sidenav opens/closes so the session viewport reclaims
    // (or yields) the space — closing keeps the element mounted, so the ViewChild doesn't refire.
    this.navService.isMenuOpened
      .pipe(untilDestroyed(this))
      .subscribe(() => this.publishSidebarWidth());

    deleteRecordingsMarkedForDeletionOnNextOpen().catch((error) => {
      console.error('Error deleting deferred local recordings:', error);
    });

    this.userService.appLoading.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value) {
        this.hideStaticLoader(); // Force hide static loader the moment the loading indicator loads for first time
      }
      this.appLoading = value;
      this.cd.detectChanges();
    });

    this.foregroundActivityService.isForegroundInactive$
      .pipe(untilDestroyed(this))
      .subscribe((isInActive) => {
        this.setupLogoutListener();
      });

    // Handle spectator room assignment when space is synced
    combineLatest([
      this.inspectModeRepository.inspectModeTargetRoomId$,
      this.spaceRepo.activeSpace$,
    ])
      .pipe(
        // Only proceed if we have a target room and space has fully synced
        filter(([targetRoomId, activeSpace]) => !!targetRoomId && !!activeSpace?.hasSpaceSynced),
        take(1), // Only set room once
        untilDestroyed(this),
      )
      .subscribe(([targetRoomId, activeSpace]) => {
        if (targetRoomId && activeSpace) {
          this.spaceRepo.setActiveSpaceCurrentRoom(targetRoomId);
          // Trigger auto-follow logic after room is set
          this.inspectModeRepository.triggerAutoFollow();
        }
      });
  }

  ngOnDestroy(): void {
    this.domListener.clear();
  }

  ngAfterViewChecked(): void {
    // increment change detection cycle
    this.telemetry.incrementMetric(CountedMetric.cd);
  }

  setupLogoutListener(): void {
    const currentFBUser = this.authService.getCurrentUser();
    if (
      (currentFBUser && this.userService.user.value?.user.email !== currentFBUser.email) ||
      (this.isListeningToUserLogout && !currentFBUser)
    ) {
      AuthTracing.log('AppComponent.setupLogoutListener', {
        reason: currentFBUser ? 'email_mismatch' : 'firebase_user_null',
      });
      this.zone.run(() => {
        this.userService.setLoginFlowLoading(true);
        window.location.reload();
      });
    }
  }

  public preventWindowZoom(e) {
    if (!e.target) {
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
    }

    // On Safari/Firefox left swipe will trigger back action - This means panning left will exit the space
    // We will prevent defaults if the broswer is Safari/Firefox (Avoid regressions as much as possible) and there is a detected swipe left
    if (
      (this.deviceAndBrowserDetectorService.isSafari() ||
        this.deviceAndBrowserDetectorService.isFirefox()) &&
      Math.abs(e.deltaX) > Math.abs(e.deltaY)
    ) {
      e.preventDefault();
    }
  }

  refresh(): void {
    this.userService.isUpdateButtonClicked.next(true);
    this.updates.activateUpdate().then((res) => {
      window.location.reload();
    });
  }

  pushGTMData(gtmTag: any): void {
    if (this.user) {
      const institutionSubscriptionPlan =
        typeof this.user.institution?.billingStatus?.subscription === 'object'
          ? this.user.institution.billingStatus.subscription.plan
          : undefined;
      gtmTag = {
        ...gtmTag,
        user_id: this.user._id,
        user_email: this.user.email,
        user_name: this.user.name,
        user_role: this.user.role ?? this.user.info?.role,
        user_institution: this.user.institution?._id,
        institution_billing_type: this.user.institution?.paywallConfig?.billingType,
        institution_subscription_status: this.user.institution?.billingStatus?.subscription_status,
        institution_subscription_plan: institutionSubscriptionPlan,
        user_creation_date: this.user.createdAt,
        isStudent: this.aclService.isStudent(this.user),
        isTeacher: this.aclService.isTeacher(this.user),
        isAdmin: this.aclService.isAdmin(this.user),
        isSuperAdmin: this.aclService.isSuperAdmin(this.user),
        personas: this.user.personas?.toString(),
      };
    }
    this.gtmService.pushTag(gtmTag);
  }

  setupTelemetry(userData?: UserData) {
    this.telemetry.identifyUser(userData);
  }

  @HostListener('window:beforeunload', ['$event'])
  public onPageUnload($event: BeforeUnloadEvent): string | undefined {
    if (this.isUploading || this.uploadService.isFileUploadInProgress()) {
      // eslint-disable-next-line frontend-rules/ngx-translate-service
      $event.returnValue = 'Leave site? Changes you made may not be saved.';
      return 'Leave site? Changes you made may not be saved.';
    }
  }

  /**
   * Listens for postMessage events from the parent window (when app is embedded in iframe).
   * Used for inspect mode and following data communication - parent can send INSPECT_MODE or FOLLOWING_DATA messages.
   * Only accepts messages from authorized recording template domains.
   */
  private onPostMessage(event: MessageEvent): void {
    const data = event.data;

    // Validate message origin - only accept messages from authorized recording template domains
    if (!this.env.recordingTemplateOrigins.includes(event.origin)) {
      return;
    }

    // Validate message structure
    if (!data || typeof data !== 'object' || !data.type) {
      return;
    }

    // Run the handler inside Angular zone to avoid regressions
    this.zone.run(() => {
      switch (data.type) {
        case PostMessageType.INSPECT_MODE:
          this.handleInspectModeMessage(event);
          break;
        case PostMessageType.FOLLOWING_DATA:
          this.handleFollowingDataMessage(event);
          break;
        case PostMessageType.RECORDING_SESSION_ENDED:
          this.handleRecordingSessionEndedMessage(event);
          break;
        default:
          return;
      }
    });
  }

  private handleInspectModeMessage(event: MessageEvent): void {
    const data = event.data;
    const message = data as InspectModeMessage;

    // Update the inspect mode repository with the inspect mode state
    // Room assignment is handled by subscription that waits for space to be synced
    this.inspectModeRepository.setInspectMode(
      message.enabled,
      message.targetRoomId,
      this.spaceRepo?.activeSpaceId,
    );
  }

  private handleFollowingDataMessage(event: MessageEvent): void {
    const data = event.data;
    const message = data as FollowingDataMessage;

    // Update the inspect mode repository with the following data state
    this.inspectModeRepository.setFollowingData(message.targetUserId, message.targetTabId);
  }

  private handleRecordingSessionEndedMessage(_event: MessageEvent): void {
    this.inspectModeRepository.stopFollowingCurrentTarget();
  }

  reset(): void {
    if (this.dragElement) {
      this.dragElement.reset();
    }
  }

  dragStarted(event: CdkDragStart): void {
    this.dragElement = event.source._dragRef;
  }

  closeCalc(): void {
    this.navService.closeCalculator();
  }

  closeGraph(): void {
    this.navService.closeGraph();
  }

  closePeriodicTable(): void {
    this.navService.closePeriodicTable();
  }

  closeSessionAnalytics(): void {
    this.navService.closeSessionAnalytics();
  }

  openNewMessageDialog(): void {
    const dialogRef = this.dialog.open(NewMessageDialogComponent, {
      panelClass: 'zero-padding-dialog',
      autoFocus: false,
      restoreFocus: false,
    });
    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((newConversation: NewConversationRequest | null | undefined) => {
        if (newConversation) {
          if (isUserConversation(newConversation)) {
            return this.handleNewUserConversation(newConversation);
          } else {
            return this.handleNewGroupConversation(newConversation);
          }
        }
      });
  }

  private handleNewUserConversation(
    newUserConversation: NewUserConversationRequest,
  ): Promise<boolean> {
    this.messagingService.addNewUserConversation(newUserConversation);
    return this.router.navigate(['messages', 'user', newUserConversation.user._id]);
  }

  private async handleNewGroupConversation(
    newGroupConversation: NewGroupConversationRequest,
  ): Promise<boolean> {
    try {
      const groupId = await lastValueFrom(
        this.messagingService.addNewGroupConversation(newGroupConversation),
      );
      return this.router.navigate(['messages', 'group', groupId]);
    } catch {
      return this.router.navigate(['messages', 'home']);
    }
  }

  isParent(): boolean {
    if (this.user) {
      return this.aclService.isParent(this.user);
    }

    return false;
  }

  showRevokedInfo(): void {
    const dialogRef = this.dialog.open(RevokedInfoDialogComponent, {
      panelClass: 'revoked-info-dialog-container',
      data: {
        user: this.user,
      },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.profilesService.hideRevokedInfo().pipe(untilDestroyed(this)).subscribe();
      });
  }

  onResize() {
    this.isMobileView = window.innerWidth <= this.uiService.MOBILE_MAX_WIDTH;
    if (this.uiService.isMobile.getValue() !== this.isMobileView) {
      this.uiService.isMobile.next(this.isMobileView);
    }
  }

  goToBillingSetting() {
    this.settingsService.showSettingsPanel(panelID.payment);
  }

  forceHideStaticLoader(url: string): boolean {
    // The loader spinner doesn't show for a set number of cases. In these cases, we need to force kill the static loader
    // The main case for this is if the sidebar does not show for the route and if the url is not a /spaces/XYZ link
    // Note: We need to filter out routes to '/', as these can automatically redirect to new routes which may have loader spinners
    return url !== '/' && !this.showSideNavBar(url) && !url.includes(`/${URL_CONSTANTS.SPACES}/`);
  }

  showSideNavBar(url: string): boolean {
    return (
      url !== '/' &&
      !url.includes('/account') &&
      !url.includes('/present') &&
      !url.includes('/view') &&
      !url.includes('/practice') &&
      // In switcher mode a space is opened beside the sidebar (rather than as a full-page
      // takeover), so the global sidebar must remain visible for /spaces/:id and the access flows.
      (!url.includes(`/${URL_CONSTANTS.SPACES}/`) || this.spacesSwitcherService.isEnabled) &&
      !url.includes('/pencilcam') &&
      !url.includes('/techcheck') &&
      !url.includes('/next') &&
      (url.includes('analytics/recordings') || !url.includes('/recording')) &&
      !this.hidesNavForSessionDetails
    );
  }

  private updateSessionDetailsNavVisibility(): void {
    const navigationState = getCodeTriggeredNavigationState<SideNavNavigationState>(this.router);
    this.hidesNavForSessionDetails =
      this.router.url.includes('/sessions/') && !navigationState?.keepSideNav;
  }

  showReferralAcceptedDialog(referralId: string, user: User, referredUser: UserInfo): void {
    const referralAcceptedParams = {
      panelClass: 'referral-wb-dialog',
      autoFocus: false,
      data: {
        referralId,
        user,
        referredUser,
      },
    };

    this.modalManagerService.showModal(ReferralAcceptedDialogComponent, referralAcceptedParams);
  }

  private checkForInstitutionUpgradeAfterFullLoad(): void {
    // Only check if query parameter exists first (quick check)
    if (
      !checkIfQueryParamSetToTrue(ActionsQueryParamKeys.POST_INSTITUTION_UPGRADE) ||
      // Handle corner case for navigating to space after page reload.
      // Space navigation events preserve URL parameters even after they are consumed and
      // removed. These parameters are fetched from old URL snapshot.
      // Handle this corner case by excluding in space url.
      // The notification was appearing again on entering any space from landing page due to
      // preserve strategy which is used with other space actions from query parameters
      this.router.url.includes('/spaces/')
    ) {
      return;
    }

    // Wait for all loading states to be complete
    combineLatest([
      this.userService.user.pipe(filter((user) => !!user?.user?._id)), // User data loaded
      this.userService.appLoading.pipe(filter((loading) => !loading)), // App loading complete
      this.userService.showLoginFlowLoader$.pipe(filter((loading) => !loading)), // Login flow complete
    ])
      .pipe(
        take(1), // Only trigger once
      )
      .subscribe(() => {
        // All loading states are complete - perfect time to show modal
        this.modalManagerService.showModal(InstitutionalPlanUpgradeSuccessModalComponent, {
          disableClose: false,
          width: '450px',
          height: 'auto',
          panelClass: 'institution-upgrade-success-modal',
        });
        removeQueryParamsFromUrl(this.location, this.telemetryService, [
          ActionsQueryParamKeys.POST_INSTITUTION_UPGRADE,
        ]);
      });
  }
}
